import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { Button, Input } from "@/components/ui";
import {
  SettingsField,
  SettingsFieldGroup,
} from "@/components/settings/SettingsSection";
import { ConnectionTestResult } from "./ConnectionTestResult";
import { useTranslation } from "@/packages/utils";

type ByomRuntime = "ollama" | "vllm" | "lmstudio" | "localai" | "other";

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

export interface ByomConfigFormState {
  runtime: ByomRuntime;
  endpointUrl: string;
  modelName: string;
  standardModelName: string;
  visionModelName: string;
  apiKey: string;
}

interface ByomConfigFormProps {
  initialConfig?: {
    byomRuntime?: ByomRuntime;
    byomEndpointUrl?: string;
    byomModelName?: string;
    byomStandardModelName?: string;
    byomVisionModelName?: string;
    byomApiKeyEncrypted?: string;
  };
  onTestResult: (result: TestResult | null) => void;
  onChange: (config: ByomConfigFormState) => void;
  onTestConnection: (config: ByomConfigFormState) => Promise<void>;
  testResult: TestResult | null;
  isTesting: boolean;
}

const RUNTIME_OPTIONS: { value: ByomRuntime }[] = [
  { value: "ollama" },
  { value: "vllm" },
  { value: "lmstudio" },
  { value: "localai" },
  { value: "other" },
];

const RUNTIME_PLACEHOLDERS: Record<
  ByomRuntime,
  { url: string; model: string; standard: string; vision: string }
> = {
  ollama: {
    url: "http://localhost:11434/v1",
    model: "llama3",
    standard: "llama3",
    vision: "llava",
  },
  vllm: {
    url: "http://localhost:8000/v1",
    model: "meta-llama/Llama-3-8B",
    standard: "meta-llama/Llama-3-8B",
    vision: "",
  },
  lmstudio: {
    url: "http://localhost:1234/v1",
    model: "loaded-model",
    standard: "loaded-model",
    vision: "",
  },
  localai: {
    url: "http://localhost:8080/v1",
    model: "gpt-4",
    standard: "gpt-4",
    vision: "",
  },
  other: {
    url: "http://your-server:port/v1",
    model: "model-name",
    standard: "model-name",
    vision: "",
  },
};

export function ByomConfigForm({
  initialConfig,
  onTestResult,
  onChange,
  onTestConnection,
  testResult,
  isTesting,
}: ByomConfigFormProps) {
  const { t } = useTranslation(["settings", "common"]);
  const [runtime, setRuntime] = useState<ByomRuntime>(
    initialConfig?.byomRuntime ?? "ollama",
  );
  const [endpointUrl, setEndpointUrl] = useState(
    initialConfig?.byomEndpointUrl ?? "",
  );
  const [modelName, setModelName] = useState(
    initialConfig?.byomModelName ?? "",
  );
  const [standardModelName, setStandardModelName] = useState(
    initialConfig?.byomStandardModelName ?? "",
  );
  const [visionModelName, setVisionModelName] = useState(
    initialConfig?.byomVisionModelName ?? "",
  );
  const [apiKey, setApiKey] = useState(
    initialConfig?.byomApiKeyEncrypted ? "***" : "",
  );

  const placeholders = RUNTIME_PLACEHOLDERS[runtime];

  const updateField = useCallback(
    (
      field: keyof ByomConfigFormState,
      value: string,
      currentState: ByomConfigFormState,
    ) => {
      const updated = { ...currentState, [field]: value };
      onChange(updated);
      onTestResult(null);
      return updated;
    },
    [onChange, onTestResult],
  );

  const handleRuntimeChange = useCallback(
    (newRuntime: ByomRuntime) => {
      setRuntime(newRuntime);
      updateField("runtime", newRuntime, {
        runtime: newRuntime,
        endpointUrl,
        modelName,
        standardModelName,
        visionModelName,
        apiKey,
      });
    },
    [
      endpointUrl,
      modelName,
      standardModelName,
      visionModelName,
      apiKey,
      updateField,
    ],
  );

  const handleEndpointUrlChange = useCallback(
    (value: string) => {
      setEndpointUrl(value);
      updateField("endpointUrl", value, {
        runtime,
        endpointUrl: value,
        modelName,
        standardModelName,
        visionModelName,
        apiKey,
      });
    },
    [
      runtime,
      modelName,
      standardModelName,
      visionModelName,
      apiKey,
      updateField,
    ],
  );

  const handleModelNameChange = useCallback(
    (value: string) => {
      setModelName(value);
      updateField("modelName", value, {
        runtime,
        endpointUrl,
        modelName: value,
        standardModelName,
        visionModelName,
        apiKey,
      });
    },
    [
      runtime,
      endpointUrl,
      standardModelName,
      visionModelName,
      apiKey,
      updateField,
    ],
  );

  const handleStandardModelNameChange = useCallback(
    (value: string) => {
      setStandardModelName(value);
      updateField("standardModelName", value, {
        runtime,
        endpointUrl,
        modelName,
        standardModelName: value,
        visionModelName,
        apiKey,
      });
    },
    [runtime, endpointUrl, modelName, visionModelName, apiKey, updateField],
  );

  const handleVisionModelNameChange = useCallback(
    (value: string) => {
      setVisionModelName(value);
      updateField("visionModelName", value, {
        runtime,
        endpointUrl,
        modelName,
        standardModelName,
        visionModelName: value,
        apiKey,
      });
    },
    [runtime, endpointUrl, modelName, standardModelName, apiKey, updateField],
  );

  const handleApiKeyChange = useCallback(
    (value: string) => {
      setApiKey(value);
      updateField("apiKey", value, {
        runtime,
        endpointUrl,
        modelName,
        standardModelName,
        visionModelName,
        apiKey: value,
      });
    },
    [
      runtime,
      endpointUrl,
      modelName,
      standardModelName,
      visionModelName,
      updateField,
    ],
  );

  const handleTestConnection = useCallback(async () => {
    await onTestConnection({
      runtime,
      endpointUrl,
      modelName,
      standardModelName,
      visionModelName,
      apiKey,
    });
  }, [
    onTestConnection,
    runtime,
    endpointUrl,
    modelName,
    standardModelName,
    visionModelName,
    apiKey,
  ]);

  return (
    <div className="space-y-5">
      {/* Runtime Selection */}
      <SettingsField
        label={t("settings:aiProvider.byom.runtime.label")}
        description={t("settings:aiProvider.byom.runtime.description")}
      >
        <div className="flex flex-wrap gap-2">
          {RUNTIME_OPTIONS.map((opt) => (
            <motion.button
              key={opt.value}
              type="button"
              onClick={() => handleRuntimeChange(opt.value)}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 border cursor-pointer ${
                runtime === opt.value
                  ? "border-accent bg-accent/8 text-accent"
                  : "border-border bg-white text-secondary hover:border-border hover:bg-card-hover hover:text-primary"
              }`}
            >
              {t(
                `settings:aiProvider.byom.runtime.options.${opt.value}` as "settings:aiProvider.byom.runtime.options.ollama",
              )}
            </motion.button>
          ))}
        </div>
      </SettingsField>

      <SettingsFieldGroup columns={1}>
        <SettingsField
          label={t("settings:aiProvider.byom.endpoint.label")}
          description={t("settings:aiProvider.byom.endpoint.description")}
        >
          <Input
            value={endpointUrl}
            onChange={(e) => handleEndpointUrlChange(e.target.value)}
            placeholder={placeholders.url}
          />
        </SettingsField>
      </SettingsFieldGroup>

      <SettingsFieldGroup columns={2}>
        <SettingsField
          label={t("settings:aiProvider.byom.fastModel.label")}
          description={t("settings:aiProvider.byom.fastModel.description")}
        >
          <Input
            value={modelName}
            onChange={(e) => handleModelNameChange(e.target.value)}
            placeholder={placeholders.model}
          />
        </SettingsField>

        <SettingsField
          label={t("settings:aiProvider.byom.standardModel.label")}
          description={t("settings:aiProvider.byom.standardModel.description")}
        >
          <Input
            value={standardModelName}
            onChange={(e) => handleStandardModelNameChange(e.target.value)}
            placeholder={placeholders.standard || t("common:labels.optional")}
          />
        </SettingsField>
      </SettingsFieldGroup>

      <SettingsFieldGroup columns={2}>
        <SettingsField
          label={t("settings:aiProvider.byom.visionModel.label")}
          description={t("settings:aiProvider.byom.visionModel.description")}
        >
          <Input
            value={visionModelName}
            onChange={(e) => handleVisionModelNameChange(e.target.value)}
            placeholder={placeholders.vision || t("common:labels.optional")}
          />
        </SettingsField>
      </SettingsFieldGroup>

      <SettingsFieldGroup columns={1}>
        <SettingsField
          label={t("settings:aiProvider.byom.apiKey.label")}
          description={t("settings:aiProvider.byom.apiKey.description")}
        >
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => handleApiKeyChange(e.target.value)}
            placeholder={t("common:labels.optional")}
          />
        </SettingsField>
      </SettingsFieldGroup>

      {/* Test Connection */}
      <div className="space-y-3">
        <Button
          variant="secondary"
          icon={Zap}
          onClick={handleTestConnection}
          loading={isTesting}
          disabled={!endpointUrl || !modelName}
        >
          {t("settings:aiProvider.byom.testConnection")}
        </Button>

        <ConnectionTestResult result={testResult} isTesting={isTesting} />
      </div>
    </div>
  );
}
